<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fcom_media_lib_overlay"
      role="dialog"
      aria-modal="true"
      @click.self="close"
    >
      <div class="fcom_media_lib_panel" @click.stop>
        <div class="fcom_media_lib_head">
          <h3 class="fcom_media_lib_title">{{ __('media_library') }}</h3>
          <button type="button" class="fcom_media_lib_close" @click="close" :aria-label="__('cancel')">
            ✕
          </button>
        </div>

        <div class="fcom_media_lib_toolbar">
          <div v-if="canUpload" class="fcom_media_lib_add">
            <input
              ref="fileInputRef"
              type="file"
              accept="image/*,video/*"
              class="fcom_media_lib_file_input"
              :disabled="uploading"
              @change="onFileSelected"
            />
            <button
              type="button"
              class="el-button fcom_primary_button fcom_media_lib_add_btn"
              :disabled="uploading || loading"
              @click="triggerFile"
            >
              {{ uploading ? __('loading') : __('add_new_media') }}
            </button>
          </div>
          <div class="fcom_media_lib_search_wrap">
            <input
              v-model="searchQ"
              type="search"
              class="fcom_media_lib_input el-input"
              :placeholder="__('search')"
              @input="onSearchInput"
            />
          </div>
        </div>

        <p v-if="error" class="fcom_media_lib_error">{{ error }}</p>
        <div v-if="loading && items.length === 0" class="fcom_media_lib_state">
          {{ __('loading') }}
        </div>
        <div v-else-if="!loading && items.length === 0" class="fcom_media_lib_state fcom_media_lib_empty_hint">
          {{ __('no_images_in_library') }}
        </div>
        <ul v-else class="fcom_media_lib_grid" role="list">
          <li v-for="att in items" :key="att.id" class="fcom_media_lib_cell" role="listitem">
            <button
              type="button"
              class="fcom_media_lib_thumb"
              :class="{ 'fcom_is_selected': isSelectedId(att.id) }"
              :aria-pressed="isSelectedId(att.id) ? 'true' : 'false'"
              :aria-label="isSelectedId(att.id) ? __('image_selected') : __('select_image')"
              @click="pick(att)"
            >
              <img
                v-if="thumbUrl(att) && !isVideoFile(att)"
                :src="thumbUrl(att)"
                :alt="att.alt_text || ''"
                loading="lazy"
                draggable="false"
              />
              <span v-else-if="isVideoFile(att)" class="fcom_vid_cell">
                <video
                  :src="att.source_url"
                  class="fcom_vid_preview"
                  muted
                  playsinline
                  preload="metadata"
                />
                <span class="fcom_vid_badge" aria-hidden="true">▶</span>
              </span>
              <span v-else class="fcom_file_fallback" />
              <span v-if="isSelectedId(att.id)" class="fcom_media_check" aria-hidden="true">✓</span>
            </button>
          </li>
        </ul>
        <div v-if="items.length > 0" class="fcom_media_lib_foot">
          <button
            v-if="page < totalPages"
            type="button"
            class="el-button fcom_secondary_button fcom_media_lib_loadmore"
            :disabled="loading"
            @click="loadMore"
          >
            {{ loading ? __('loading') : __('load_more') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, onUnmounted, computed } from 'vue'
import { __ } from '@/utils/i18n.js'
import { isVideoMime, parseRestErrorMessage } from '@/utils/media.js'

const props = defineProps({
  open: { type: Boolean, default: false },
  /** Attachment ID currently used as the event featured image (for highlight in grid). */
  selectedId: { type: [Number, String], default: null },
})

const emit = defineEmits(['update:open', 'select', 'close'])

const config = window.FcaEventsConfig || {}

const canUpload = computed(
  () => !Object.prototype.hasOwnProperty.call(config, 'canBrowseMedia') || config.canBrowseMedia,
)

const loading = ref(false)
const uploading = ref(false)
const error = ref(null)
const items = ref([])
const page = ref(1)
const totalPages = ref(1)
const searchQ = ref('')
const fileInputRef = ref(null)
let searchDebounce = null
let onKeydown = null

function isSelectedId(id) {
  if (props.selectedId == null || props.selectedId === '' || id == null) return false
  return String(id) === String(props.selectedId)
}

function mediaEndpointUrl() {
  const raw = (config.wpV2Base || '').trim()
  if (!raw) return null
  const base = raw.endsWith('/') ? raw : raw + '/'
  try {
    return new URL('media', base)
  } catch {
    return null
  }
}

function isVideoFile(attachment) {
  return isVideoMime(attachment?.mime_type)
}

function thumbUrl(attachment) {
  if (isVideoFile(attachment)) {
    const fromDetails = attachment.media_details?.image?.src || attachment.media_details?.sizes?.thumbnail?.source_url
    if (fromDetails) {
      return fromDetails
    }
    return null
  }
  const sizes = attachment.media_details?.sizes
  if (sizes?.thumbnail?.source_url) {
    return sizes.thumbnail.source_url
  }
  if (sizes?.medium?.source_url) {
    return sizes.medium.source_url
  }
  return attachment.source_url
}

function displayUrl(attachment) {
  if (isVideoFile(attachment)) {
    return attachment.source_url
  }
  const sizes = attachment.media_details?.sizes
  if (sizes?.large?.source_url) {
    return sizes.large.source_url
  }
  if (sizes?.medium?.source_url) {
    return sizes.medium.source_url
  }
  return attachment.source_url
}

function dedupeById(list) {
  const seen = new Set()
  return list.filter((a) => {
    if (seen.has(a.id)) return false
    seen.add(a.id)
    return true
  })
}

async function loadItems(append) {
  const u = mediaEndpointUrl()
  if (!u) {
    error.value = __('media_list_error')
    return
  }
  loading.value = true
  error.value = null
  if (!append) {
    page.value = 1
  }
  u.search = ''
  u.searchParams.set('per_page', '24')
  u.searchParams.set('page', String(page.value))
  u.searchParams.set('_fields', 'id,source_url,alt_text,media_details,mime_type')
  if (searchQ.value.trim()) {
    u.searchParams.set('search', searchQ.value.trim())
  }

  try {
    const res = await fetch(u.toString(), {
      headers: { 'X-WP-Nonce': config.nonce },
      credentials: 'same-origin',
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(parseRestErrorMessage(data, `HTTP ${res.status}`))
    }
    const data = await res.json()
    const tp = res.headers.get('X-WP-TotalPages')
    totalPages.value = tp ? Math.max(1, parseInt(tp, 10)) : 1
    if (append) {
      items.value = dedupeById(items.value.concat(data))
    } else {
      items.value = data
    }
  } catch (e) {
    error.value = e?.message || __('media_list_error')
    if (!append) items.value = []
  } finally {
    loading.value = false
  }
}

function onSearchInput() {
  clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => {
    page.value = 1
    loadItems(false)
  }, 400)
}

function loadMore() {
  if (page.value >= totalPages.value || loading.value) return
  page.value += 1
  loadItems(true)
}

function pick(attachment) {
  emit('select', {
    id: attachment.id,
    url: displayUrl(attachment),
    mime: attachment.mime_type || null,
  })
  close()
}

function close() {
  emit('update:open', false)
  emit('close')
}

function triggerFile() {
  fileInputRef.value?.click()
}

async function onFileSelected(e) {
  const file = e?.target?.files?.[0]
  if (e?.target) e.target.value = ''
  if (!file || !canUpload.value) return

  const u = mediaEndpointUrl()
  if (!u) {
    error.value = __('media_list_error')
    return
  }

  error.value = null
  uploading.value = true

  const fd = new FormData()
  fd.append('file', file, file.name)

  try {
    const res = await fetch(u.toString(), {
      method: 'POST',
      body: fd,
      headers: { 'X-WP-Nonce': config.nonce },
      credentials: 'same-origin',
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(parseRestErrorMessage(data, `HTTP ${res.status}`))
    }
    const att = await res.json()
    if (!att || !att.id) {
      error.value = __('image_upload_failed')
      return
    }
    if (att && att.id) {
      items.value = dedupeById([att, ...items.value])
    }
    emit('select', {
      id: att.id,
      url: displayUrl(att) || att.source_url,
      mime: att.mime_type || null,
    })
    /* Keep modal open so the new item shows with the selection checkmark; user closes with ✕. */
  } catch (e) {
    error.value = e?.message || __('image_upload_failed')
  } finally {
    uploading.value = false
  }
}

watch(
  () => props.open,
  (v) => {
    if (v) {
      searchQ.value = ''
      error.value = null
      loadItems(false)
      onKeydown = (e) => {
        if (e.key === 'Escape') close()
      }
      document.addEventListener('keydown', onKeydown)
    } else {
      if (onKeydown) {
        document.removeEventListener('keydown', onKeydown)
        onKeydown = null
      }
    }
  },
)

onUnmounted(() => {
  if (onKeydown) document.removeEventListener('keydown', onKeydown)
  clearTimeout(searchDebounce)
})
</script>

<style scoped>
.fcom_media_lib_overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}
.fcom_media_lib_panel {
  width: 100%;
  max-width: 640px;
  max-height: min(85vh, 640px);
  display: flex;
  flex-direction: column;
  background: var(--fcom-primary-bg, #fff);
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  border: 1px solid var(--fcom-primary-border, #e5e7eb);
}
.fcom_media_lib_head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--fcom-primary-border, #e5e7eb);
}
.fcom_media_lib_title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}
.fcom_media_lib_close {
  border: none;
  background: transparent;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  padding: 4px 8px;
  color: var(--fcom-text-muted, #6b7280);
}
.fcom_media_lib_close:hover {
  color: var(--fcom-text, #111);
}
.fcom_media_lib_toolbar {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 16px 0;
}
@media (min-width: 480px) {
  .fcom_media_lib_toolbar {
    flex-direction: row;
    align-items: center;
  }
  .fcom_media_lib_search_wrap {
    flex: 1;
    min-width: 0;
  }
}
.fcom_media_lib_add {
  display: flex;
  align-items: center;
  gap: 8px;
}
.fcom_media_lib_file_input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}
.fcom_media_lib_add_btn {
  white-space: nowrap;
  font-size: 13px;
  padding: 8px 14px;
}
.fcom_media_lib_input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 14px;
}
.fcom_media_lib_error {
  margin: 8px 16px 0;
  color: #b91c1c;
  font-size: 13px;
}
.fcom_media_lib_state {
  padding: 24px 16px;
  text-align: center;
  color: var(--fcom-text-muted, #6b7280);
  font-size: 14px;
}
.fcom_media_lib_empty_hint {
  padding-top: 8px;
}
.fcom_media_lib_grid {
  list-style: none;
  margin: 12px 0 0;
  padding: 0 10px 12px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  overflow-y: auto;
  min-height: 120px;
  max-height: 42vh;
}
@media (min-width: 500px) {
  .fcom_media_lib_grid {
    grid-template-columns: repeat(5, 1fr);
  }
}
.fcom_media_lib_cell {
  margin: 0;
  padding: 0;
  aspect-ratio: 1;
}
.fcom_media_lib_thumb {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 2px solid var(--fcom-primary-border, #e5e7eb);
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background: #f3f4f6;
  display: block;
  box-sizing: border-box;
}
.fcom_media_lib_thumb:hover {
  border-color: var(--fcom-text-link, #3b82f6);
}
.fcom_media_lib_thumb.fcom_is_selected {
  border-color: var(--fcom-text-link, #2563eb);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.35);
}
.fcom_media_check {
  position: absolute;
  right: 4px;
  top: 4px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #2563eb;
  color: #fff;
  font-size: 12px;
  line-height: 22px;
  text-align: center;
  font-weight: 700;
  pointer-events: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}
.fcom_media_lib_thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  vertical-align: middle;
}
.fcom_media_lib_foot {
  padding: 0 16px 14px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
}
.fcom_media_lib_loadmore {
  width: 100%;
  max-width: 280px;
}
.fcom_vid_cell {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  min-height: 40px;
}
.fcom_vid_preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  background: #111;
}
.fcom_vid_badge {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 18px;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  pointer-events: none;
}
.fcom_file_fallback {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 40px;
  background: linear-gradient(135deg, #e5e7eb, #f3f4f6);
}
</style>
